import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { NumberCell } from '../../components/NumberCell/NumberCell';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { ColumnsSettingsViewDict, ColumnViewInfo } from '../../typings/common';
import { Banner, Campaign, Group, Phrase } from '../../typings/domain';
import { Goal, GoalDecl } from '../../typings/goals';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridGroupedColumn,
    GridSourceParams,
    OptionalGridColumnDecl
} from '../../typings/grid';

import * as keyset from './goals.i18n';

const i18n = i18nFactory(keyset);

export enum GroupedColumnKey {
    GOALS = 'GOALS'
}

export enum GoalColumnKey {
    STAT_GOALS = 'STAT_GOALS',
    STAT_CONVERSION_RATE = 'STAT_CONVERSION_RATE',
    STAT_COST_PER_ACTION = 'STAT_COST_PER_ACTION'
}

export function getGoalsDecl(goals?: Record<string, Goal>): Array<GoalDecl> {
    if (!goals) {
        return [];
    }

    return Object.keys(goals).reduce((acc: Array<GoalDecl>, goalKey) => {
        acc.push({
            key: goals[goalKey].id,
            title: goals[goalKey].name
        });
        return acc;
    }, []);
}

export function getGroupedColumnsGoals(
    goalIds: string[],
    currency: CurrencyCode,
    columnsSettings: ColumnsSettingsViewDict,
    sourceParams: GridSourceParams,
    goals?: Record<string, Goal>
): Array<GridGroupedColumn<Campaign | Group | Banner | Phrase>> {
    const { view } = sourceParams;
    const currencySymbol = mapStringToCurrencySymbol(currency);

    return goalIds
        .reduce(
            (
                acc: Array<
                    GridGroupedColumn<Campaign | Group | Banner | Phrase>
                >,
                goalId
            ) => {
                const groupedColumnsTitle =
                    goals && goals[goalId] && goals[goalId].name;

                const columns: GridColumnDecl<
                    Campaign | Group | Banner | Phrase
                >[] = [
                    {
                        width: 100,
                        isResizable: false,
                        title: i18n('column-title-conversion'),
                        align: 'right',
                        key: `${GoalColumnKey.STAT_GOALS}_${goalId}`,
                        sortKey: GoalColumnKey.STAT_GOALS,
                        goalId: goalId,
                        CellComponent: React.memo(
                            ({
                                rowData: { goalStats }
                            }: GridCellProps<
                                Campaign | Group | Banner | Phrase
                            >) => {
                                const currentGoalstat =
                                    goalStats && goalStats[goalId];
                                const statValue = currentGoalstat
                                    ? currentGoalstat.goals
                                    : 0;

                                return <NumberCell num={statValue} />;
                            }
                        )
                    },
                    {
                        width: 110,
                        isResizable: false,
                        title: i18n('column-title-percent-conversion'),
                        align: 'right',
                        key: `${GoalColumnKey.STAT_CONVERSION_RATE}_${goalId}`,
                        sortKey: GoalColumnKey.STAT_CONVERSION_RATE,
                        goalId: goalId,
                        CellComponent: React.memo(
                            ({
                                rowData: { goalStats }
                            }: GridCellProps<
                                Campaign | Group | Banner | Phrase
                            >) => {
                                const currentGoalstat =
                                    goalStats && goalStats[goalId];
                                const statValue = currentGoalstat
                                    ? currentGoalstat.conversionRate
                                    : 0;

                                return <NumberCell num={statValue} />;
                            }
                        )
                    },
                    {
                        width: 80,
                        isResizable: false,
                        title: i18n('column-title-cpa', {
                            currency: currencySymbol
                        }),
                        align: 'right',
                        key: `${GoalColumnKey.STAT_COST_PER_ACTION}_${goalId}`,
                        sortKey: GoalColumnKey.STAT_COST_PER_ACTION,
                        goalId: goalId,
                        CellComponent: React.memo(
                            ({
                                rowData: { goalStats }
                            }: GridCellProps<
                                Campaign | Group | Banner | Phrase
                            >) => {
                                const currentGoalstat =
                                    goalStats && goalStats[goalId];
                                const statValue = currentGoalstat
                                    ? currentGoalstat.costPerAction
                                    : 0;

                                return <NumberCell num={statValue} />;
                            }
                        )
                    }
                ];

                acc.push({
                    title: groupedColumnsTitle,
                    key: `${GroupedColumnKey.GOALS}_${goalId}`,
                    isGoalIcon: true,
                    columns,
                    goalId
                });

                return acc;
            },
            []
        )
        .map(groupedColumn => {
            return {
                ...groupedColumn,
                ...columnsSettings[view].columnViewInfo[groupedColumn.key]
            };
        });
}

export function getGoalsColumnsDecl<T>(
    hasSelectedGoalsAtColumnsSettingsEditor?: boolean,
    isColumnsSettingsEditorLocation?: boolean
): Array<OptionalGridColumnDecl<T, {}, {}>> {
    return [
        {
            title: i18n('goals-statistics'),
            key: GroupedColumnKey.GOALS,
            isAvailable: (): boolean => {
                return Boolean(
                    !isColumnsSettingsEditorLocation ||
                        (isColumnsSettingsEditorLocation &&
                            hasSelectedGoalsAtColumnsSettingsEditor)
                );
            },
            CellComponent: React.memo(({ rowData }) => {
                return <>{null}</>;
            })
        }
    ];
}

export function getDefaultGoalsViewInfo(): ColumnViewInfo {
    return {
        // GOALS является группировкой, а не колонкой, поэтому задавать width не нужно
        [GroupedColumnKey.GOALS]: {
            width: 0,
            widthCompact: 0,
            hidden: false
        }
    };
}
